import { Phone } from 'lucide-react';

import { Btn } from '@/Components/Btn';
import { Headshot } from '@/Components/Headshot';
import { Stars } from '@/Components/Stars';
import type { LawyerCardData } from '@/Types/lawyer';

/**
 * Compact one-line lawyer row. Small headshot, name, city, tiny stars, phone
 * icon button. Used inside map detail rails, "related lawyers" lists, lookup
 * autocomplete results.
 *
 *   <LawyerCardC data={lawyer} />
 */

export interface LawyerCardCProps {
    data: LawyerCardData;
    className?: string;
}

export function LawyerCardC({ data, className }: LawyerCardCProps) {
    const cls = [
        'flex items-center gap-3 bg-card border border-rule rounded-xl p-3',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <article className={cls} aria-label={`${data.name} — ${data.firm}`}>
            <Headshot
                initials={data.initials}
                hue={data.hue}
                size={56}
                radius={8}
            />
            <div className="flex-1 min-w-0">
                <div className="font-serif text-[15px] font-medium leading-tight">
                    {data.name}
                </div>
                <div className="text-[12px] text-ink-mute mt-0.5">
                    {data.city}, {data.state}
                </div>
                <div className="mt-1">
                    <Stars
                        value={data.rating}
                        reviews={data.reviews}
                        size={11}
                    />
                </div>
            </div>
            <Btn
                kind="secondary"
                size="sm"
                icon={<Phone size={14} aria-hidden="true" />}
                aria-label={`Call ${data.name}`}
            >
                <span className="sr-only">Call</span>
            </Btn>
        </article>
    );
}

export default LawyerCardC;
